/**
 * Markup sub-view for an attachment in pane-3 — a temporary REPLACEMENT
 * of BodyPreview's content view. Activated by the green "markup" badge
 * on an attachment card in the matter timeline (pane-2). The user
 * returns to BodyPreview by clicking the attachment card again.
 *
 * Layout (per Ken's spec):
 *   Header row:   "MARKUP" label (left)   Download/CSV + Copy (right)
 *   Filename label row above column headers (left-aligned in first cell)
 *   Table columns: LOC. | TYPE | MARKUP (action pill + text) | NOTE
 *
 * Data sources:
 *   - artifact.redlines[]  → docx_markup.py shape:
 *     {id, action, text, paragraph_context, sort_order, ...}
 *   - artifact.comments[]  → docx_markup.py shape:
 *     {id, content, paragraph_context, sort_order, ...}
 *
 * LOC. derivation: paragraph_context is the paragraph TEXT not a number,
 * so we sniff it for section / paragraph / page hints. When nothing
 * matches we render an em-dash. See deriveLocation().
 *
 * NOTE column: reads `characterization` (Ken's spec name). The field
 * doesn't exist in current data — markup is still raw extraction. Will
 * populate once an AI step writes it. Renders em-dash in the meantime.
 */

import { useMemo, useState } from 'react'
import { Download, Copy, Check } from 'lucide-react'

export type MarkupRow = {
  key: string
  source: 'redline' | 'comment'
  author: string
  loc: string
  // Quoted anchor / surrounding text, shown in the LOC column when no numeric
  // location can be derived. Google Docs comments carry no paragraph location,
  // only the highlighted span — so the anchor text IS the "where" (Emerson
  // 2026-08-04).
  anchor: string
  action: '' | 'insert' | 'delete' | 'move_from' | 'move_to'
  text: string
  note: string
  sortKey: number
}

/** Effective location string: the derived numeric location when we have one,
 *  otherwise the quoted anchor text, otherwise an em-dash. */
export function effectiveLoc(r: MarkupRow): string {
  if (r.loc !== '—') return r.loc
  return r.anchor || '—'
}

type Props = {
  filename: string
  redlines: Array<Record<string, unknown>>
  comments: Array<Record<string, unknown>>
}

/** Best-effort location string from paragraph_context paragraph text.
 *  Match a leading numbered heading (1, 1.2, 1.2.3), an explicit
 *  "Section/Article N", a "Paragraph N", or "page N" / "p. N" anywhere
 *  in the paragraph. Returns '—' on no hit. */
function deriveLocation(paragraphContext: string): string {
  const t = (paragraphContext || '').trim()
  if (!t) return '—'
  // Leading numbered heading: "3.5 Foo bar..." or "5. Bar baz..."
  const lead = t.match(/^(\d+(?:\.\d+){0,3})\.?\s+[A-Z]/)
  if (lead) return `§ ${lead[1]}`
  // Explicit "Section N" / "Article N" / "§ N"
  const sect = t.match(/\b(?:Section|Article|§)\s*(\d+(?:\.\d+){0,3})\b/i)
  if (sect) return `§ ${sect[1]}`
  // "Paragraph N"
  const para = t.match(/\bParagraph\s*(\d+(?:\.\d+){0,3})\b/i)
  if (para) return `❡ ${para[1]}`
  // "page N" / "p. N"
  const page = t.match(/\b(?:page|p\.)\s*(\d+)\b/i)
  if (page) return `p. ${page[1]}`
  return '—'
}

export function buildRows(
  redlines: Array<Record<string, unknown>>,
  comments: Array<Record<string, unknown>>,
): MarkupRow[] {
  const rows: MarkupRow[] = []
  // Two id conventions coexist:
  //   - Raw docx extractor:  id
  //   - LLM enrichment step: redline_id / comment_id
  // and two "note text" conventions for comments:
  //   - Raw docx extractor:  content
  //   - LLM enrichment step: text
  // Read both; source of truth is whichever exists first.
  for (const r of redlines || []) {
    const pc = String(r.paragraph_context || r.surrounding_context || '')
    const action = String(r.action || '') as MarkupRow['action']
    const rid = r.id ?? r.redline_id
    rows.push({
      key: `r-${String(rid != null ? rid : rows.length)}`,
      source: 'redline',
      author: String(r.author || r.author_name || ''),
      loc: deriveLocation(pc),
      // No numeric location → the surrounding paragraph text locates it.
      anchor: pc,
      action: action,
      text: String(r.text || ''),
      note: String(r.characterization || ''),
      sortKey: Number(r.sort_order) || rows.length,
    })
  }
  for (const c of comments || []) {
    // Numeric location comes from the paragraph context only; the quoted
    // anchor is the fallback shown when there's no section/page number
    // (the case for every Google Docs comment).
    const pc = String(c.paragraph_context || c.surrounding_context || '')
    const cid = c.id ?? c.comment_id
    rows.push({
      key: `c-${String(cid != null ? cid : rows.length)}`,
      source: 'comment',
      author: String(c.author || c.author_name || ''),
      loc: deriveLocation(pc),
      anchor: String(c.quoted_text || c.quoted || pc || ''),
      action: '',
      text: String(c.content || c.text || ''),
      note: String(c.characterization || ''),
      sortKey: Number(c.sort_order) || rows.length,
    })
  }
  // T9 #6: order by SECTION NUMBER (§2, §4, §6, §7, §8 …), not document
  // order — so the markup table reads as a clause-by-clause ledger. Rows
  // whose location carries a § number sort first, ascending by that number
  // (dotted sub-sections compared component-wise, e.g. §4 before §4.2); rows
  // with no section (Google-Docs comments anchored to quoted text, ❡/page
  // locations) fall to the bottom, keeping their original document order
  // (sort_order) among themselves.
  const sectionVal = (r: MarkupRow): number[] | null => {
    const m = r.loc.match(/§\s*(\d+(?:\.\d+)*)/)
    return m ? m[1].split('.').map(Number) : null
  }
  const cmpNums = (a: number[], b: number[]): number => {
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      const d = (a[i] ?? 0) - (b[i] ?? 0)
      if (d) return d
    }
    return 0
  }
  rows.sort((a, b) => {
    const sa = sectionVal(a), sb = sectionVal(b)
    if (sa && sb) return cmpNums(sa, sb) || (a.sortKey - b.sortKey) || a.source.localeCompare(b.source)
    if (sa) return -1
    if (sb) return 1
    return (a.sortKey - b.sortKey) || a.source.localeCompare(b.source)
  })
  return rows
}

/** Quote a CSV cell — wrap in double-quotes if it contains comma,
 *  newline, or a double-quote; double-quote any embedded quotes. */
function csvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`
  return v
}

export function rowsToCsv(filename: string, rows: MarkupRow[]): string {
  const header = ['LOC.', 'TYPE', 'ACTION', 'TEXT', 'NOTE']
  const lines: string[] = []
  lines.push(`# ${csvCell(filename)}`)
  lines.push(header.map(csvCell).join(','))
  for (const r of rows) {
    lines.push([effectiveLoc(r), r.source, r.action, r.text, r.note].map(csvCell).join(','))
  }
  return lines.join('\n')
}

export function rowsToTsv(filename: string, rows: MarkupRow[]): string {
  // Clipboard format: tab-separated so it pastes cleanly into a
  // spreadsheet. Newlines inside cells become spaces (TSV doesn't
  // have a robust quoting convention).
  const flat = (v: string) => v.replace(/[\t\r\n]+/g, ' ')
  const lines: string[] = []
  lines.push(filename)
  lines.push(['LOC.', 'TYPE', 'ACTION', 'TEXT', 'NOTE'].join('\t'))
  for (const r of rows) {
    lines.push([flat(effectiveLoc(r)), r.source, r.action, flat(r.text), flat(r.note)].join('\t'))
  }
  return lines.join('\n')
}

export function ActionWord({ action }: { action: MarkupRow['action'] }) {
  if (!action) return null
  // Ken 2026-07-02 revision: plain colored word — no pill / border /
  // background. Blue for insert, orange for delete; move variants
  // fall back to the muted text color.
  const color =
    action === 'insert' ? 'text-[#4ea3ff]'
    : action === 'delete' ? 'text-[#ff9a3d]'
    : 'me-text-mute'
  return (
    <span className={`me-mono text-[11px] lowercase ${color}`} title={action}>
      {action}
    </span>
  )
}

export default function MarkupTable({ filename, redlines, comments }: Props) {
  const rows = useMemo(() => buildRows(redlines, comments), [redlines, comments])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [copied, setCopied] = useState(false)

  const toggle = (k: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  const onDownload = () => {
    const csv = rowsToCsv(filename, rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename.replace(/\.docx?$/i, '')}-markup.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const onCopy = async () => {
    const tsv = rowsToTsv(filename, rows)
    try {
      await navigator.clipboard.writeText(tsv)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard denied — silent fail matches BodyPreview's onCopy */
    }
  }

  return (
    <div>
      <div className="me-section-h flex items-center justify-between gap-2">
        <span>MARKUP</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onDownload}
            className="me-btn me-btn-outline"
            title="Download this markup table as CSV"
            disabled={rows.length === 0}
          >
            <Download size={11} /> download
          </button>
          <button
            onClick={onCopy}
            className="me-btn me-btn-outline"
            title="Copy the markup table to the clipboard (TSV — paste into a spreadsheet)"
            disabled={rows.length === 0}
          >
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md bg-[color:var(--me-panel)] p-4 text-[12px] me-text-mute italic">
          No markup items extracted for this attachment.
        </div>
      ) : (
        <div className="rounded-md border me-border overflow-hidden">
          {/* Filename label row — left-aligned in first cell of a row
              above the table header, per spec. */}
          <div className="px-3 py-2 text-[11.5px] me-text-dim border-b me-border bg-[color:var(--me-panel)]">
            <span className="me-mono">{filename || '(unnamed attachment)'}</span>
          </div>
          {/* table-fixed + explicit column widths so the MARKUP text
              cell has a bounded width — required for the ellipsis to
              engage. Auto-layout ignored width hints and grew the
              cell to fit its content, which is why Ken saw no
              truncation before (Issue 4). */}
          <table className="w-full table-fixed text-[11.5px]">
            <colgroup>
              <col style={{ width: '10%' }} />{/* LOC.  */}
              <col style={{ width: '9%' }} />{/* TYPE  */}
              <col style={{ width: '10%' }} />{/* action */}
              <col />{/* text — takes remaining width */}
              <col style={{ width: '22%' }} />{/* NOTE */}
            </colgroup>
            <thead>
              <tr className="bg-[color:var(--me-panel)] me-text-mute uppercase tracking-wider me-mono text-[10px]">
                <th className="text-left px-3 py-1.5">LOC.</th>
                <th className="text-left px-3 py-1.5">TYPE</th>
                <th className="text-left px-3 py-1.5" colSpan={2}>MARKUP</th>
                <th className="text-left px-3 py-1.5">NOTE</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = expanded.has(r.key)
                // When there's no numeric location, the LOC cell shows the
                // quoted anchor text (prose, so not me-mono; quoted + italic to
                // read as a snippet). Full text on hover.
                const locIsAnchor = r.loc === '—' && !!r.anchor
                return (
                  <tr
                    key={r.key}
                    onClick={() => toggle(r.key)}
                    className="border-t me-border align-top cursor-pointer hover:bg-[color:var(--me-panel)]"
                  >
                    <td
                      className={`px-3 py-1.5 me-text-dim whitespace-nowrap overflow-hidden text-ellipsis ${locIsAnchor ? 'italic' : 'me-mono'}`}
                      title={locIsAnchor ? r.anchor : undefined}
                    >
                      {locIsAnchor ? `“${r.anchor}”` : r.loc}
                    </td>
                    <td className="px-3 py-1.5 me-text-dim">
                      <span className="me-mono lowercase">{r.source}</span>
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <ActionWord action={r.action} />
                    </td>
                    <td className="px-3 py-1.5 me-text leading-snug overflow-hidden">
                      {/* Truncation depends on table-fixed above +
                          this cell having a computed width. When
                          collapsed: single-line ellipsis. When
                          expanded: wrap freely, break long words. */}
                      {isOpen ? (
                        <span className="block" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.text || <em className="me-text-mute">(empty)</em>}
                        </span>
                      ) : (
                        <span
                          className="block overflow-hidden text-ellipsis whitespace-nowrap"
                          title={r.text}
                        >
                          {r.text || <em className="me-text-mute">(empty)</em>}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 me-text-dim overflow-hidden">
                      {r.note
                        ? <span className="block" style={{ wordBreak: 'break-word' }}>{r.note}</span>
                        : <span className="me-text-mute">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
